import { CloudOff, Inbox, Lock, SearchX, TriangleAlert, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/cn'
import { Button } from './Button'
import { CodeText } from './format'
import type { ActionDef } from './types'

export interface EmptyStateProps {
  /** vier verschiedene Fälle, nie derselbe Text (design-system §6.5) */
  variant: 'first-use' | 'no-results' | 'no-permission' | 'error' | 'offline'
  /** 20 px in einer 40-px-Fläche in sunken */
  icon?: LucideIcon
  title: string
  description?: string
  action?: ActionDef
  secondaryAction?: ActionDef
  /** 'sm' in Karten und Tabellen */
  size?: 'sm' | 'md'
  /** technische Kennung zum Kopieren (Variante 'error') */
  reference?: string
  className?: string
}

const ICONS: Record<EmptyStateProps['variant'], LucideIcon> = {
  'first-use': Inbox,
  'no-results': SearchX,
  'no-permission': Lock,
  error: TriangleAlert,
  offline: CloudOff,
}

/** Leerzustand: linksbündig im Inhaltsbereich, ohne Bild und ohne Illustration. */
export function EmptyState({
  variant,
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
  reference,
  className,
}: EmptyStateProps) {
  const Icon = icon ?? ICONS[variant]
  const small = size === 'sm'
  return (
    <div
      role={variant === 'error' ? 'alert' : undefined}
      className={cn(
        'flex max-w-xl items-start text-left',
        small ? 'gap-3 p-4' : 'flex-col gap-4 px-(--page-px) py-10',
        className,
      )}
      data-ui="empty-state"
      data-variant={variant}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-md bg-sunken',
          small ? 'size-8' : 'size-10',
          variant === 'error' ? 'text-danger-fg' : 'text-fg-muted',
        )}
        aria-hidden
      >
        <Icon className={small ? 'size-4' : 'size-5'} strokeWidth={1.75} />
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <p className={cn('font-semibold text-fg', small ? 'text-dense' : 'text-body')}>{title}</p>
        {description && <p className={cn('text-fg-muted', small ? 'text-caption' : 'text-body')}>{description}</p>}
        {(action || secondaryAction) && (
          <div className={cn('flex flex-wrap items-center gap-2', small ? 'mt-2' : 'mt-3')}>
            {action && (
              <Button
                variant={action.variant ?? (variant === 'first-use' ? 'primary' : 'secondary')}
                size={small ? 'sm' : 'md'}
                icon={action.icon}
                onClick={() => void action.onSelect()}
                disabled={action.disabled}
                disabledReason={action.disabledReason}
              >
                {action.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant={secondaryAction.variant ?? 'ghost'}
                size={small ? 'sm' : 'md'}
                icon={secondaryAction.icon}
                onClick={() => void secondaryAction.onSelect()}
                disabled={secondaryAction.disabled}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
        {reference && (
          <p className="mt-2 flex items-center gap-1 text-caption text-fg-subtle">
            Kennung <CodeText copyable>{reference}</CodeText>
          </p>
        )}
      </div>
    </div>
  )
}

export interface ErrorStateProps {
  /** Was ist passiert? – „Liste konnte nicht geladen werden“ */
  title?: string
  /** Was bedeutet das, was kann ich tun? */
  message?: string
  onRetry?: () => void
  retryLabel?: string
  /** technische Kennung, klein und kopierbar */
  reference?: string
  size?: 'sm' | 'md'
  className?: string
}

/** Fehler eines Bereichs – an Stelle des Inhalts, mit „Erneut versuchen“. */
export function ErrorState({
  title = 'Der Bereich konnte nicht geladen werden',
  message,
  onRetry,
  retryLabel = 'Erneut versuchen',
  reference,
  size,
  className,
}: ErrorStateProps) {
  return (
    <EmptyState
      variant="error"
      title={title}
      description={message}
      size={size}
      reference={reference}
      className={className}
      action={onRetry ? { id: 'retry', label: retryLabel, onSelect: onRetry } : undefined}
    />
  )
}
